import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AddressAutocomplete from '../../components/common/AddressAutocomplete';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';
import styles from '../user/CompleteProfilePage.module.css';

/**
 * Two-step setup for new family/spouse/child members:
 *   Step 1: Complete profile (creates TeleMed ID)
 *   Step 2: Create password
 * 
 * Accessed via welcome email link: /setup-profile?token=RESET_TOKEN
 */
const SetupProfilePage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState('profile'); // 'profile' | 'saving' | 'password' | 'done'
  const [saving, setSaving] = useState(false);
  const [token, setToken] = useState('');
  const [userId, setUserId] = useState('');
  const [tokenValid, setTokenValid] = useState(null); // null=loading, true, false
  const [userName, setUserName] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phone: '',
    gender: '',
    weight: '',
    heightFeet: '',
    heightInches: '',
    address: '',
    state: '',
    city: '',
    zip: ''
  });

  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  // Extract and validate token on mount
  useEffect(() => {
    const t = searchParams.get('token');
    if (!t) {
      setTokenValid(false);
      return;
    }
    setToken(t);

    // Validate token and get user info
    api.post('/auth/validate-reset-token', { token: t })
      .then(res => {
        // If profile is already complete, redirect to login
        if (res.isProfileComplete) {
          toast('Your profile is already set up. Please log in.', 'info');
          navigate('/login', { replace: true });
          return;
        }
        setTokenValid(true);
        setUserId(res.userId);
        setUserName(res.firstName || '');
        if (res.firstName) {
          setFormData(prev => ({ ...prev, firstName: res.firstName }));
        }
        if (res.lastName) {
          setFormData(prev => ({ ...prev, lastName: res.lastName }));
        }
        if (res.dateOfBirth) {
          setFormData(prev => ({ ...prev, dateOfBirth: res.dateOfBirth.split('T')[0] }));
        }
        if (res.phone) {
          setFormData(prev => ({ ...prev, phone: res.phone }));
        }
      })
      .catch(() => {
        setTokenValid(false);
      });
  }, [searchParams]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressSelect = ({ street, city, state, zipCode }) => {
    setFormData(prev => ({
      ...prev,
      address: street || prev.address,
      city: city || prev.city,
      state: state || prev.state,
      zip: zipCode || prev.zip
    }));
  };

  const isProfileValid = () => {
    return formData.firstName && formData.lastName && formData.dateOfBirth &&
           formData.phone && formData.gender && formData.weight &&
           formData.heightFeet && formData.heightInches !== '' &&
           formData.address && formData.state && formData.city && formData.zip;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!isProfileValid()) {
      toast('Please fill in all required fields', 'error');
      return;
    }

    setSaving(true);
    setStep('saving');

    // Safety timeout: if stuck on spinner for 30s, go back to form
    const safetyTimeout = setTimeout(() => {
      setSaving(false);
      setStep('profile');
      toast('Request timed out. Please try again.', 'error');
    }, 30000);

    try {
      const heightCombined = `${formData.heightFeet}'${formData.heightInches}"`;

      const profilePayload = {
        token,
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
        phone: formData.phone,
        gender: formData.gender,
        weight: formData.weight,
        height: heightCombined,
        address: {
          street: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zip
        }
      };

      await api.post('/auth/setup-profile', profilePayload);
      clearTimeout(safetyTimeout);
      toast('Profile saved! Now create your password.', 'success');
      setStep('password');
    } catch (error) {
      clearTimeout(safetyTimeout);
      const msg = error.message || 'Failed to save profile. Please try again.';
      toast(msg, 'error');
      setStep('profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordErrors({});

    const errors = {};
    if (!passwordData.password) {
      errors.password = 'Password is required';
    } else if (passwordData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (passwordData.password !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setSaving(true);
    try {
      await api.post('/auth/reset-password', {
        accessToken: token,
        password: passwordData.password
      });
      toast('Password created! You can now log in.', 'success');
      setStep('done');
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (error) {
      const msg = error.message || 'Failed to set password. Please try again.';
      toast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (tokenValid === null) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.creatingState}>
            <div className={styles.spinner} />
            <h2>Verifying your link...</h2>
          </div>
        </div>
      </div>
    );
  }

  // Invalid/expired token
  if (tokenValid === false) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <h1 className={styles.title}>Link Expired</h1>
            <p className={styles.subtitle}>
              This setup link has expired or is invalid. Please contact your family member or support to get a new one.
            </p>
          </div>
          <button className={styles.submitBtn} onClick={() => navigate('/login')}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <img src="/oval-logo.svg" alt="Oval Care" className={styles.logo} onError={(e) => { e.target.style.display = 'none'; }} />
          {step === 'password' || step === 'done' ? (
            <>
              <h1 className={styles.title}>Create Your Password</h1>
              <p className={styles.subtitle}>
                Almost done{userName ? `, ${userName}` : ''}! Create a password to complete your account setup.
              </p>
            </>
          ) : (
            <>
              <h1 className={styles.title}>Welcome to OVAL{userName ? `, ${userName}` : ''}!</h1>
              <p className={styles.subtitle}>
                Let's set up your profile. We need a few details for your care account. Your address is used for prescriptions and shipping.
              </p>
            </>
          )}

          {/* Step indicator */}
          {step !== 'done' && (
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', margin: '16px 0 8px' }}>
              <div style={{
                width: '40px', height: '4px', borderRadius: '2px',
                background: step === 'profile' || step === 'saving' ? '#e07a3a' : '#ccc',
                transition: 'background 0.3s'
              }} />
              <div style={{
                width: '40px', height: '4px', borderRadius: '2px',
                background: step === 'password' ? '#e07a3a' : '#ccc',
                transition: 'background 0.3s'
              }} />
            </div>
          )}
          <p style={{ fontSize: '13px', color: '#888', margin: 0 }}>
            {step === 'profile' || step === 'saving' ? 'Step 1 of 2: Your Profile' : step === 'password' ? 'Step 2 of 2: Create Password' : ''}
          </p>
        </div>

        {step === 'saving' ? (
          <div className={styles.creatingState}>
            <div className={styles.spinner} />
            <h2>Setting up your account...</h2>
            <p>We're verifying your information and creating your care profile.</p>
          </div>
        ) : step === 'done' ? (
          <div className={styles.creatingState}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10003;</div>
            <h2>You're all set!</h2>
            <p>Redirecting you to login...</p>
          </div>
        ) : step === 'password' ? (
          <form onSubmit={handlePasswordSubmit} className={styles.form}>
            <div className={styles.field}>
              <label className={styles.label}>New Password <span className={styles.required}>*</span></label>
              <input
                type="password"
                value={passwordData.password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="At least 8 characters"
                className={styles.input}
                style={passwordErrors.password ? { borderColor: '#e74c3c' } : {}}
                required
              />
              {passwordErrors.password && <span style={{ color: '#e74c3c', fontSize: '13px' }}>{passwordErrors.password}</span>}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Confirm Password <span className={styles.required}>*</span></label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                placeholder="Re-enter your password"
                className={styles.input}
                style={passwordErrors.confirmPassword ? { borderColor: '#e74c3c' } : {}}
                required
              />
              {passwordErrors.confirmPassword && <span style={{ color: '#e74c3c', fontSize: '13px' }}>{passwordErrors.confirmPassword}</span>}
            </div>

            <button
              type="submit"
              className={styles.submitBtn}
              disabled={saving}
            >
              {saving ? 'Creating...' : 'Create Password & Finish'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleProfileSubmit} className={styles.form}>
            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>First Name <span className={styles.required}>*</span></label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  placeholder="Enter first name"
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Last Name <span className={styles.required}>*</span></label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  placeholder="Enter last name"
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>Date of Birth <span className={styles.required}>*</span></label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Phone <span className={styles.required}>*</span></label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="Enter phone number"
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>Gender <span className={styles.required}>*</span></label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleChange('gender', e.target.value)}
                  className={styles.select}
                  required
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Weight (lbs) <span className={styles.required}>*</span></label>
                <input
                  type="number"
                  value={formData.weight}
                  onChange={(e) => handleChange('weight', e.target.value)}
                  placeholder="e.g., 180"
                  className={styles.input}
                  min="50"
                  max="800"
                  required
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>Height <span className={styles.required}>*</span></label>
                <div className={styles.heightRow}>
                  <div className={styles.heightField}>
                    <select
                      value={formData.heightFeet}
                      onChange={(e) => handleChange('heightFeet', e.target.value)}
                      className={styles.select}
                      required
                    >
                      <option value="">Feet</option>
                      <option value="3">3 ft</option>
                      <option value="4">4 ft</option>
                      <option value="5">5 ft</option>
                      <option value="6">6 ft</option>
                      <option value="7">7 ft</option>
                    </select>
                  </div>
                  <div className={styles.heightField}>
                    <select
                      value={formData.heightInches}
                      onChange={(e) => handleChange('heightInches', e.target.value)}
                      className={styles.select}
                      required
                    >
                      <option value="">Inches</option>
                      <option value="0">0 in</option>
                      <option value="1">1 in</option>
                      <option value="2">2 in</option>
                      <option value="3">3 in</option>
                      <option value="4">4 in</option>
                      <option value="5">5 in</option>
                      <option value="6">6 in</option>
                      <option value="7">7 in</option>
                      <option value="8">8 in</option>
                      <option value="9">9 in</option>
                      <option value="10">10 in</option>
                      <option value="11">11 in</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.divider}>
              <span>Address</span>
            </div>

            <div className={styles.fieldFull}>
              <AddressAutocomplete
                value={formData.address}
                onChange={(val) => handleChange('address', val)}
                onSelect={handleAddressSelect}
                placeholder="Start typing your address..."
                label="Street Address"
              />
            </div>

            <div className={styles.row3}>
              <div className={styles.field}>
                <label className={styles.label}>State <span className={styles.required}>*</span></label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="e.g., FL"
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>City <span className={styles.required}>*</span></label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="e.g., Miami"
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>ZIP <span className={styles.required}>*</span></label>
                <input
                  type="text"
                  value={formData.zip}
                  onChange={(e) => handleChange('zip', e.target.value)}
                  placeholder="e.g., 33101"
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className={styles.submitBtn}
              disabled={saving || !isProfileValid()}
            >
              {saving ? 'Saving...' : 'Continue to Password Setup'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default SetupProfilePage;
